/**
 * Auto Apply Product Branch Override
 *
 * Función que sobreescribe el destino de donde se manda el negocio
 * según reglas específicas de productos, especialmente para IVF MORE
 */

/**
 * Aplica automáticamente las reglas de override de sucursal por producto
 * @param {string} articulo - Nombre del artículo/producto
 * @param {object} basePayload - Payload base con información de sucursales
 * @returns {object} - Objeto con idSedeDestino y nombreSedeDestino actualizados
 */
export function autoApplyProductBranchOverride(articulo, basePayload) {
    console.log(`🔍 Evaluando override de sucursal para: ${articulo}`)

    // Por defecto, usar la sucursal de destino original
    let idSedeDestino = basePayload.idSedeDestino
    let nombreSedeDestino = basePayload.nombreSedeDestino
    let requiresSpecialHandling = false

    // Identificar conjunto actual basado en sessionStorage
    const currentArticle = sessionStorage.getItem('Article')
    const isIvfMoreSet = currentArticle?.includes('IVF MORE NUEVO')
    const isMoreSaSet = currentArticle?.includes('MORE SA NUEVO')

    console.log(
        `🔍 Conjunto actual: ${currentArticle} (IVF MORE Set: ${isIvfMoreSet}, MORE SA Set: ${isMoreSaSet})`
    )

    // Reglas específicas por producto y conjunto
    if (isIvfMoreSet) {
        // Reglas para conjunto IVF MORE NUEVO
        if (articulo === 'OVUM MORE O' || articulo === 'IVF MORE') {
            // En IVF MORE NUEVO, tanto OVUM MORE O como IVF MORE van a origen
            idSedeDestino = basePayload.idSedeOrigen
            nombreSedeDestino = basePayload.nombreSedeOrigen
            requiresSpecialHandling = true
            console.log(
                `🏢 Conjunto IVF MORE: ${articulo} → Sucursal de ORIGEN (${nombreSedeDestino})`
            )
        } else if (articulo === 'OVUM MORE D') {
            // OVUM MORE D siempre a CDMX
            idSedeDestino = 'INGEN'
            nombreSedeDestino = 'Ciudad de Mexico'
            requiresSpecialHandling = true
            console.log(`🏥 Conjunto IVF MORE: ${articulo} → CDMX (FORZADO)`)
        }
    } else if (isMoreSaSet) {
        // Reglas para conjunto MORE SA NUEVO
        if (articulo === 'MORE') {
            idSedeDestino = 'INGEN'
            nombreSedeDestino = 'Ciudad de Mexico'
            requiresSpecialHandling = true
            console.log(`🏥 Conjunto MORE SA: ${articulo} → CDMX (FORZADO)`)
        } else if (articulo === 'IVF MORE') {
            // En MORE SA NUEVO, IVF MORE va a origen
            idSedeDestino = basePayload.idSedeOrigen
            nombreSedeDestino = basePayload.nombreSedeOrigen
            requiresSpecialHandling = true
            console.log(
                `🏢 Conjunto MORE SA: ${articulo} → Sucursal de ORIGEN (${nombreSedeDestino})`
            )
        }
    }

    return {
        idSedeDestino,
        nombreSedeDestino,
        requiresSpecialHandling,
        originalDestination: {
            idSedeDestino: basePayload.idSedeDestino,
            nombreSedeDestino: basePayload.nombreSedeDestino
        }
    }
}

export function getHardcodedPipelineSettings(articulo) {
    // Settings específicos para productos que van a CDMX
    const cdmxSettings = {
        pipelineId: '819372',
        dealstage: '2949134'
    }

    // Identificar conjunto actual
    const currentArticle = sessionStorage.getItem('Article')
    const isIvfMoreSet = currentArticle?.includes('IVF MORE NUEVO')
    const isMoreSaSet = currentArticle?.includes('MORE SA NUEVO')

    console.log(`🔍 Verificando pipeline para ${articulo} en conjunto: ${currentArticle}`)

    if (isIvfMoreSet) {
        // Para conjunto IVF MORE NUEVO
        if (articulo === 'OVUM MORE D') {
            console.log(`🔧 Pipeline CDMX para OVUM MORE D en conjunto IVF MORE`)
            return cdmxSettings
        }
        // Los demás productos (OVUM MORE O, IVF MORE) mantienen su pipeline original
        console.log(`🔧 ${articulo} mantiene pipeline original en conjunto IVF MORE`)
        return null
    } else if (isMoreSaSet) {
        // Para conjunto MORE SA NUEVO
        if (articulo === 'MORE') {
            console.log(`🔧 Pipeline CDMX para MORE en conjunto MORE SA`)
            return cdmxSettings
        }
        // IVF MORE mantiene su pipeline original
        console.log(`🔧 ${articulo} mantiene pipeline original en conjunto MORE SA`)
        return null
    }

    console.log(`🔍 No hay pipeline hardcodeado para ${articulo}, usando valores del conjunto`)
    return null
}

/**
 * Obtiene parámetros adicionales específicos para HubSpot por producto
 * @param {string} articulo - Nombre del artículo/producto
 * @returns {object} - Parámetros adicionales para el payload
 */
export function getHubSpotAdditionalParams(articulo) {
    const additionalParams = {}

    switch (articulo) {
        case 'OVUM MORE D':
        case 'MORE':
            additionalParams.destination_branch = 'CDMX'
            console.log(`🎯 Parámetros adicionales para HubSpot - ${articulo}:`, additionalParams)
            break

        // Agregar más productos aquí si necesitan parámetros adicionales
    }

    return additionalParams
}

/**
 * Función principal que combina todas las reglas de override
 * @param {string} articulo - Nombre del artículo/producto
 * @param {object} basePayload - Payload base
 * @returns {object} - Configuración completa del producto
 */
export function applyCompleteProductOverride(articulo, basePayload) {
    // 1. Aplicar override de sucursal
    const branchOverride = autoApplyProductBranchOverride(articulo, basePayload)

    // 2. Obtener configuración de pipeline hardcodeado
    const pipelineSettings = getHardcodedPipelineSettings(articulo)

    // 3. Obtener parámetros adicionales para HubSpot
    const hubspotParams = getHubSpotAdditionalParams(articulo)

    return {
        ...branchOverride,
        hardcodedPipeline: pipelineSettings,
        hubspotAdditionalParams: hubspotParams
    }
}

export default {
    autoApplyProductBranchOverride,
    getHardcodedPipelineSettings,
    getHubSpotAdditionalParams,
    applyCompleteProductOverride
}
